/** The employee's own record.
 *
 * Shows what `GET /api/portal/me/` actually returns — name, email,
 * organisation, and whether they can see their colleagues' tickets — and says
 * plainly that department, manager and teams are not modelled.
 *
 * The brief asked for those fields. `core.Contact` has name, email, phone,
 * organisation and the identity columns, and nothing else: no department, no
 * manager, no team membership for a non-agent. Rendering empty rows labelled
 * "Department —" would imply the data exists and is merely unset, which is a
 * different and more misleading claim than "we do not model this". So the
 * missing half is named as missing.
 */
import { Building2, Mail, ShieldCheck, User, Users } from 'lucide-react'

import { PageHeader } from '../../components/layout/AppShell'
import { Badge } from '../../components/ui/badge'
import { Card } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/primitives'
import { useMe } from '../auth'

export function Profile() {
  const { data: me, isLoading } = useMe()

  if (isLoading || !me) {
    return (
      <div>
        <PageHeader title="Profile" description="Your details, as IT holds them." />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Profile" description="Your details, as IT holds them." />

      <Card className="overflow-hidden rounded-xl">
        <div className="flex items-center gap-4 border-b border-border bg-gradient-to-br from-primary/10 to-surface p-5">
          <span
            className="grid size-14 shrink-0 place-items-center rounded-full bg-primary/15 text-lg font-semibold text-primary"
            aria-hidden
          >
            {(me.name || me.email)
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0]?.toUpperCase() ?? '')
              .join('')}
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold">{me.name || me.email}</h2>
            <p className="truncate text-sm text-muted-foreground">{me.email}</p>
          </div>
        </div>

        <dl className="divide-y divide-border">
          <Row icon={User} label="Name" value={me.name || '—'} />
          <Row icon={Mail} label="Email" value={me.email} />
          <Row icon={Building2} label="Organisation" value={me.organization ?? '—'} />
          <Row
            icon={ShieldCheck}
            label="Account type"
            value={me.is_external ? 'External contact' : 'Employee'}
          />
          <Row
            icon={Users}
            label="Ticket visibility"
            value={
              me.can_view_org_tickets
                ? 'You can see your organisation’s tickets'
                : 'You can see your own tickets'
            }
          />
        </dl>
      </Card>

      {/* Named as absent rather than rendered as blank rows. */}
      <Card className="rounded-xl p-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Not held yet</h3>
          <Badge variant="warning" size="sm">
            No model
          </Badge>
        </div>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Department and team membership are not stored against your record. They are not
          blank — the platform does not model them yet, so the portal does not pretend to.
        </p>
        <ul className="mt-3 space-y-1.5">
          {[
            'Department and manager — would need fields on core.Contact',
            // Notification preferences moved out of this list when
            // core.NotificationPreference shipped — they are edited under
            // Notifications → Preferences, not here.
            'My teams — core.TeamMember exists but has no portal endpoint',
          ].map((item) => (
            <li key={item} className="flex gap-2 text-xs text-muted-foreground">
              <span className="mt-1 size-1 shrink-0 rounded-full bg-border" aria-hidden />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Ask the service desk to correct anything above that is wrong — these details come
          from your Microsoft account and are not editable here.
        </p>
      </Card>
    </div>
  )
}

function Row({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof User
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3 px-5 py-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <dt className="w-40 shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm font-medium">{value}</dd>
    </div>
  )
}
